/**
 * Reading focus — shared helpers for "assigned reading" links.
 *
 * An instructor can scope a chapter link to part of the chapter:
 *
 *   /books/:book/assets/:asset?sections=0.1-0.8&questions=1-5,8-10
 *
 * `sections` selects chapter sections by the numbers the chapter-outline
 * controller displays (so what the student sees in the outline is what goes in
 * the link), or by heading slug (the outline's anchor id without its
 * `section-` prefix) when a number is ambiguous. `questions` selects review
 * exercises by their printed question number. Everything else in the chapter is
 * FOLDED into collapsed accordions (or dimmed with `focus=dim`), never removed —
 * the student can still see it is one chapter and open any part, cross
 * references keep resolving, and a mistyped link degrades to a normal chapter.
 *
 * Token grammar (comma separated):
 *   3          section 3 and all of its sub-sections
 *   3.0        section 3's heading + lead-in text only (before its first sub-section)
 *   3.2        sub-section 3.2 only (its parent heading stays visible)
 *   0.1-0.8    inclusive range, compared as (major, minor) pairs
 *   3-4        sections 3 and 4 with all sub-sections
 *   the-appraisal-profession         an un-numbered section (heading slug), whole
 *   the-appraisal-profession:intro   that section's heading + lead-in only
 *   all        no filtering (same as omitting the parameter)
 *
 * The numbers come from the DOM at page load, which is why this lives in the
 * front end: the server never numbers sections. See docs/book-sections.md.
 */

export const SECTIONS_PARAM = "sections"
export const QUESTIONS_PARAM = "questions"
// `focus=dim` keeps unassigned parts in the flow at reduced opacity instead of
// folding them into accordions (the default).
export const FOCUS_PARAM = "focus"

export function paramFromUrl(name) {
  try {
    return new URLSearchParams(window.location.search).get(name) || ""
  } catch {
    return ""
  }
}

// "0.1-0.8,3,the-appraisal-profession" → { ranges, slugs, all, empty, raw }
export function parseSpec(str) {
  const raw = (str || "").trim()
  const spec = { ranges: [], slugs: new Set(), all: false, empty: raw === "", raw }
  if (spec.empty) return spec
  if (raw.toLowerCase() === "all") {
    spec.all = true
    return spec
  }

  raw.split(/[,;\s]+/).filter(Boolean).forEach((token) => {
    const m = token.match(/^(\d+(?:\.\d+)?)(?:-(\d+(?:\.\d+)?))?$/)
    if (m) {
      spec.ranges.push([toTuple(m[1], "start"), toTuple(m[2] || m[1], "end")])
    } else {
      spec.slugs.add(token.toLowerCase().replace(/^section-/, ""))
    }
  })
  return spec
}

// "3" → [3, 0] at the start of a range, [3, ∞] at the end, so a bare major
// number covers all of its sub-sections. "0.8" → [0, 8] either way.
export function toTuple(number, edge = "start") {
  const [major, minor] = String(number).split(".")
  if (minor === undefined) return [Number(major), edge === "end" ? Infinity : 0]
  return [Number(major), Number(minor)]
}

function compare(a, b) {
  return a[0] - b[0] || a[1] - b[1]
}

// Does the spec select a unit identified by a (major, minor) tuple and/or slug?
export function specMatches(spec, { tuple, slug } = {}) {
  if (spec.all) return true
  if (spec.empty) return false
  if (tuple && spec.ranges.some(([start, end]) => compare(start, tuple) <= 0 && compare(tuple, end) <= 0)) {
    return true
  }
  return Boolean(slug && spec.slugs.has(slug.toLowerCase()))
}

export function specMatchesNumber(spec, n) {
  return specMatches(spec, { tuple: [Number(n), 0] })
}

// Human-readable summary for the banner: "0.1–0.8, 3" (en-dash ranges).
export function describeSpec(spec) {
  if (spec.empty || spec.all) return ""
  return spec.raw
    .split(/[,;\s]+/)
    .filter(Boolean)
    .map((token) => token.replace(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/, "$1–$2").replace(/:intro$/, " (intro)"))
    .join(", ")
}
